import { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';

import Card from 'components/_shared/Card';
import CTAButton3 from 'components/_shared/buttons/CTAButton3';
import Overlay from 'components/_shared/Overlay';
import Notes from 'components/holders_list/components/ReferralComponents/Notes';
import StatusLabel from './StatusLabel';

import { Caseload, UniqueMemberInfo } from 'types';
import { UnifiedReferralModel } from '../models/UnifiedReferralModel';
import type { ReferralParticipant } from '../models/UnifiedReferralModel';

import { formatDate } from 'util/index';

import { makeStyles } from '@mui/styles';

const useStyles = makeStyles(() => ({
    activeCaseload: {
        color: '#A6A6BF',
        fontWeight: 600,
        textAlign: 'center',
        marginTop: '8px'
    },
    arrow: {
        display: 'flex',
        alignItems: 'center',
        alignSelf: 'center',
        justifyContent: 'center',
        backgroundColor: '#E7EFFE',
        borderRadius: '100%',
        color: '#062D7A',
        height: '32px',
        margin: '0 64px',
        minHeight: '32px',
        minWidth: '32px',
        width: '32px',
        '@media (max-width: 768px)': {
            margin: '0 32px'
        }
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        paddingTop: '16px'
    },
    card: {
        position: 'relative',
        margin: '0 !important'
    },
    dateContainer: {
        display: 'flex',
        justifyContent: 'space-between'
    },
    date: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        color: '#5A6A81',
        fontSize: '14px'
    },
    divider: {
        borderBottom: '1px solid #E0E0E0',
        marginTop: '16px'
    },
    fromTo: {
        fontWeight: 300,
        fontSize: '16px'
    },
    infoContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
    },
    nameContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        overflow: 'hidden'
    },
    name: {
        display: 'flex',
        gap: '4px',
        fontSize: '24px',
        fontWeight: 700,
        paddingLeft: '8px',
        '& span': {
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        },
        '@media (max-width: 768px)': {
            flexDirection: 'column'
        }
    },
    nameOrgContainer: {
        display: 'flex',
        flexDirection: 'column'
    },
    photo: {
        borderRadius: '50%',
        width: '96px',
        height: '96px',
        objectFit: 'cover'
    },
    referralCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '12px'
    },
    referralInfoContainer: {
        display: 'flex'
    },
    referralInfo: {
        display: 'flex',
        flexDirection: 'column',
        flex: '1',
        gap: '8px'
    },
    referralNameOrg: {
        fontWeight: 600,
        fontSize: '24px'
    },
    referralSubOrg: {
        color: '#5A6A81',
        fontSize: '18px',
        fontWeight: 400
    },
    statusContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        marginTop: '16px'
    },
    statusTitle: {
        fontWeight: 600,
        '& i': {
            marginRight: '8px'
        }
    }
}));

type ReferralCardProps = {
    acceptReferral: (referralID: number) => void;
    caseload?: Caseload;
    homeless: UniqueMemberInfo;
    profile: boolean;
    referral: UnifiedReferralModel;
    rejectReferral: (referralID: number) => void;
    removeMemberFromMyMembers: (caseloadID: number, rejected?: boolean) => void;
    saveMemberToMyMembers: (
        caseloadID: number,
        memberID: number,
        accepted?: boolean
    ) => void;
};

const handleActionWithDelay = async (
    actionFunction: any,
    successCallback: () => void,
    setLoading: (loading: boolean) => void,
    setShowOverlay: (showOverlay: boolean) => void
) => {
    setLoading(true);
    setShowOverlay(true);

    await new Promise((resolve) => {
        actionFunction();
        setTimeout(resolve, 1000);
    });

    setLoading(false);
    successCallback();
    setTimeout(() => setShowOverlay(false), 2000);
};

const ReferralParticipantInfo: React.FC<{ participant: ReferralParticipant }> = ({ participant }) => {
    const classes = useStyles();

    if (participant.type === 'organization') {
        return (
            <span className={classes.referralNameOrg}>
                {participant.name}
            </span>
        );
    }

    return (
        <div className={classes.nameOrgContainer}>
            <span className={classes.referralNameOrg}>
                {participant.name}
            </span>
            <span className={classes.referralSubOrg}>
                {participant.subtext}
            </span>
        </div>
    );
};

const ReferralCard: React.FC<ReferralCardProps> = ({
    acceptReferral,
    caseload,
    profile,
    homeless,
    referral,
    rejectReferral,
    removeMemberFromMyMembers,
    saveMemberToMyMembers
}) => {
    const classes = useStyles();
    const history = useHistory();

    const { mobile_photo, name } = homeless;
    const {
        id: referralID,
        note,
        referrer,
        referee,
        created_at,
        statusText,
        shouldShowButtons,
        isStatusContainer,
        isActiveCaseload
    } = referral;

    const [loading, setLoading] = useState(false);
    const [accepted, setAccepted] = useState(false);
    const [rejected, setRejected] = useState(false);
    const [showOverlay, setShowOverlay] = useState(false);

    const handleAcceptReferral = useCallback(() => {
        if (!caseload) return;
        
        const accept = async () => {
            await handleActionWithDelay(
                () => {
                    acceptReferral(referralID);
                    saveMemberToMyMembers(caseload.id, homeless.id, true);
                },
                () => setAccepted(true),
                setLoading,
                setShowOverlay
            );
        };

        accept();
    }, [
        acceptReferral,
        caseload,
        homeless.id,
        referralID,
        saveMemberToMyMembers
    ]);

    const handleRejectReferral = useCallback(() => {
        if (!caseload) return;
        
        const reject = async () => {
            await handleActionWithDelay(
                () => {
                    rejectReferral(referralID);
                    removeMemberFromMyMembers(caseload.id, true);
                },
                () => setRejected(true),
                setLoading,
                setShowOverlay
            );
        };

        reject();
    }, [caseload, referralID, rejectReferral, removeMemberFromMyMembers]);

    const handleViewProfile = useCallback(() => {
        history.push(`/holders/${homeless.id}`);
    }, [history, homeless.id]);

    if (!homeless) {
        return null;
    }

    return (
        <Card id="referral-card" className={classes.card}>
            {(accepted || loading || rejected) && showOverlay && (
                <Overlay
                    loading={loading}
                    text={
                        accepted
                            ? 'Referral accepted!'
                            : rejected
                              ? 'Referral rejected!'
                              : 'Loading...'
                    }
                />
            )}
            <div className={classes.referralCard}>
                {!profile && (
                    <div className={classes.infoContainer}>
                        <img
                            className={classes.photo}
                            src={mobile_photo}
                            alt="homeless"
                        />
                        <div className={classes.nameContainer}>
                            <div className={classes.name}>
                                <span>{name}</span>
                            </div>
                            <CTAButton3
                                arrow
                                invert
                                tighter
                                onClick={handleViewProfile}
                            >
                                View Profile
                            </CTAButton3>
                        </div>
                    </div>
                )}
                <div className={classes.referralInfoContainer}>
                    <div className={classes.referralInfo}>
                        <span className={classes.fromTo}>From</span>
                        <ReferralParticipantInfo participant={referrer} />
                    </div>
                    <div className={classes.arrow}>
                        <i className="fas fa-arrow-right" />
                    </div>
                    <div className={classes.referralInfo}>
                        <span className={classes.fromTo}>To</span>
                        {referee && <ReferralParticipantInfo participant={referee} />}
                    </div>
                </div>
                <div className={classes.dateContainer}>
                    <span className={classes.date}>
                        <i className="far fa-calendar-alt" />
                        Created on: {formatDate(created_at)}
                    </span>
                    <StatusLabel status={statusText} />
                </div>
                {note && (
                    <>
                        <span className={classes.divider} />
                        <Notes note={note} />
                    </>
                )}
                {isActiveCaseload && (
                    <>
                        <span className={classes.divider} />
                        <span className={classes.activeCaseload}>
                            Already in your caseload - no action needed
                        </span>
                    </>
                )}
                {isStatusContainer && (
                    <>
                        <span className={classes.divider} />
                        <div className={classes.statusContainer}>
                            <span className={classes.statusTitle}>
                                <i className="far fa-user" />
                                Status
                            </span>
                            <span>
                                This referral has been accepted by someone. Add
                                to your caseload as well?
                            </span>
                        </div>
                    </>
                )}
                {shouldShowButtons && (
                    <>
                        {!isStatusContainer && (
                            <span className={classes.divider} />
                        )}
                        <div className={classes.buttonContainer}>
                            <CTAButton3
                                invert
                                tight
                                cancel
                                onClick={handleRejectReferral}
                            >
                                {!isStatusContainer
                                    ? 'Reject Referral'
                                    : 'Skip Referral'}
                            </CTAButton3>
                            <CTAButton3 onClick={handleAcceptReferral}>
                                {!isStatusContainer
                                    ? 'Accept Referral'
                                    : 'Add to My Caseload'}
                            </CTAButton3>
                        </div>
                    </>
                )}
            </div>
        </Card>
    );
};

export default ReferralCard;
